import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import GenericSelect from '../../components/ui/GenericSelect';
import { useHrAnalyticsApi } from './config';

// Spec §12'nin yorum katmanının EKRANI: anlatı (12a), metin temaları (12b), anomali (12e)
// ve spec §9.3'ün model kalitesi raporu.
//
// Dört servis de backend'de aylarca DI'a kayıtlıydı ama hiçbir uçtan erişilemiyordu; uçlar
// eklendikten sonra da bu sayfa yazılmadan kullanıcı için yoklardı. Sayfanın taşıdığı asıl
// sorumluluk BİÇİMSEL DÜRÜSTLÜK: bu katmanın her çıktısı "ön tarama"dır, bulgu değil, ve
// ekran bunu okuyanın gözünden kaçırmamalı.
//
// Hipotezler BU SAYFADA DEĞİL: bir uyarıya bağlıdırlar (GET /v1/ha/alerts/{id}/hypotheses) ve
// yerleri Erken Uyarılar ekranındaki kanıt çekmecesidir — uyarıdan kopuk bir hipotez listesi
// "neyin hipotezi" sorusunu cevapsız bırakırdı.
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.surface.card};
  border-radius: ${({ theme }) => theme.metrics.radius.large};
  box-shadow: 0 1px 4px rgba(${({ theme }) => theme.colors.shadow.rgb}, 0.06);
  padding: 20px 24px;
`;

const Title = styled.h2`
  margin: 0 0 4px 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Description = styled.p`
  margin: 0 0 16px 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.muted};
  max-width: 760px;
`;

const StateMessage = styled.div`
  padding: 32px 16px;
  text-align: center;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const NarrativeText = styled.p`
  margin: 0 0 12px 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: pre-wrap;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  margin: 0 6px 6px 0;
  border-radius: ${({ theme }) => theme.metrics.radius.small};
  background: ${({ theme }) => theme.colors.bgColor};
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};

  th,
  td {
    padding: 8px 10px;
    text-align: left;
    border-bottom: 1px solid ${({ theme }) => theme.colors.surface.border};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  th {
    color: ${({ theme }) => theme.colors.text.muted};
    font-weight: ${({ theme }) => theme.metrics.font.weight.medium};
  }
`;

const Controls = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const Scroller = styled.div`
  overflow-x: auto;
`;

/** Backend "ön tarama" damgasını taşıyor; ekran onu görünür kılmak zorunda. */
const Preliminary = styled.div`
  margin-bottom: 12px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const yuzde = (v) => (v === null || v === undefined ? '—' : `%${(v * 100).toFixed(1)}`);
const ondalik = (v, basamak = 3) =>
  v === null || v === undefined ? '—' : Number(v).toFixed(basamak);

export default function HrAnalyticsInsightsPage() {
  const { t, i18n } = useTranslation();
  const { getNarrative, getTextThemes, getAnomalies, getModelQuality, getCatalog } =
    useHrAnalyticsApi();

  const [narrative, setNarrative] = useState(null);
  const [narrativeState, setNarrativeState] = useState('loading');
  const [themes, setThemes] = useState([]);
  const [themesState, setThemesState] = useState('loading');
  const [quality, setQuality] = useState(null);
  const [qualityState, setQualityState] = useState('loading');

  const [metricOptions, setMetricOptions] = useState([]);
  const [metricCode, setMetricCode] = useState('');
  const [anomaly, setAnomaly] = useState(null);
  const [anomalyState, setAnomalyState] = useState('idle');

  const tarih = useMemo(
    () => (d) => (d ? new Date(d).toLocaleDateString(i18n.language) : '—'),
    [i18n.language]
  );

  // Bileşen KAPALIYSA backend 409 döner ve Axios interceptor'ı zaten kullanıcıya bildirir;
  // burada ayrıca toast YOK (CLAUDE.md). Sayfa yalnız "gösterecek bir şey yok" durumunu
  // ayırt eder — "kapalı" ile "veri yok" aynı ekrana düşmemeli.
  useEffect(() => {
    let cancelled = false;
    getNarrative()
      .then((res) => {
        if (cancelled) return;
        setNarrative(res?.data?.data ?? null);
        setNarrativeState('ready');
      })
      .catch(() => { if (!cancelled) setNarrativeState('unavailable'); });
    return () => { cancelled = true; };
  }, [getNarrative]);

  useEffect(() => {
    let cancelled = false;
    getTextThemes()
      .then((res) => {
        if (cancelled) return;
        setThemes(res?.data?.data ?? []);
        setThemesState('ready');
      })
      .catch(() => { if (!cancelled) setThemesState('unavailable'); });
    return () => { cancelled = true; };
  }, [getTextThemes]);

  useEffect(() => {
    let cancelled = false;
    getModelQuality({})
      .then((res) => {
        if (cancelled) return;
        setQuality(res?.data?.data ?? null);
        setQualityState('ready');
      })
      .catch(() => { if (!cancelled) setQualityState('unavailable'); });
    return () => { cancelled = true; };
  }, [getModelQuality]);

  // Anomali için metrik listesi KATALOGDAN geliyor — burada elle bir liste tutmak, katalog
  // değiştiğinde sessizce eskiyen ikinci bir kaynak olurdu. Yalnız gerçekten kullanılabilir
  // metrikler seçilebilir; kilitli bir metriği seçtirip sonra "veri yok" demek yanıltıcı olur.
  useEffect(() => {
    let cancelled = false;
    getCatalog()
      .then((res) => {
        if (cancelled) return;
        const items = (res?.data?.data ?? []).filter((m) => m.state === 'available');
        // Seçicide HAM KOD gösterilmiyor — kullanıcı `absence.unplanned.rate`'ten ne
        // ölçüldüğünü çıkaramaz. Çevirisi yoksa kod olduğu gibi kalır.
        setMetricOptions(items.map((m) => ({
          id: m.code,
          name: t(`hrAnalytics.metricNames.${m.code}`, m.code),
        })));
        if (items.length > 0) setMetricCode(items[0].code);
      })
      .catch(() => { /* interceptor bildirir */ });
    return () => { cancelled = true; };
  }, [getCatalog, t]);

  useEffect(() => {
    if (!metricCode) return undefined;
    let cancelled = false;
    setAnomalyState('loading');
    getAnomalies({ metricCode, scope: 'tenant' })
      .then((res) => {
        if (cancelled) return;
        setAnomaly(res?.data?.data ?? null);
        setAnomalyState('ready');
      })
      .catch(() => { if (!cancelled) setAnomalyState('unavailable'); });
    return () => { cancelled = true; };
  }, [getAnomalies, metricCode]);

  const anomaliNoktalari = anomaly?.points ?? [];
  const kalite = quality?.quality ?? null;

  return (
    <Container>
      <Card>
        <Title>{t('hrAnalytics.insights.narrativeTitle')}</Title>
        <Description>{t('hrAnalytics.insights.narrativeDescription')}</Description>
        {narrativeState === 'loading' ? (
          <StateMessage>{t('common.loading')}</StateMessage>
        ) : narrativeState === 'unavailable' ? (
          <StateMessage>{t('hrAnalytics.insights.componentUnavailable')}</StateMessage>
        ) : !narrative ? (
          <StateMessage>{t('hrAnalytics.insights.noNarrative')}</StateMessage>
        ) : (
          <>
            <NarrativeText>{narrative.text}</NarrativeText>
            <Preliminary>
              {t('hrAnalytics.insights.narrativePeriod', { date: tarih(narrative.periodStart) })}
            </Preliminary>
            {(narrative.referencedMetricCodes ?? []).map((c) => (
              <Badge key={c}>{c}</Badge>
            ))}
          </>
        )}
      </Card>

      <Card>
        <Title>{t('hrAnalytics.insights.themesTitle')}</Title>
        <Description>{t('hrAnalytics.insights.themesDescription')}</Description>
        <Preliminary>{t('hrAnalytics.insights.preliminaryNotice')}</Preliminary>
        {themesState === 'loading' ? (
          <StateMessage>{t('common.loading')}</StateMessage>
        ) : themesState === 'unavailable' ? (
          <StateMessage>{t('hrAnalytics.insights.componentUnavailable')}</StateMessage>
        ) : themes.length === 0 ? (
          <StateMessage>{t('hrAnalytics.insights.noThemes')}</StateMessage>
        ) : (
          <Scroller>
            <Table>
              <thead>
                <tr>
                  <th>{t('hrAnalytics.insights.columns.source')}</th>
                  <th>{t('hrAnalytics.insights.columns.theme')}</th>
                  <th>{t('hrAnalytics.insights.columns.sentiment')}</th>
                  <th>{t('hrAnalytics.insights.columns.responseCount')}</th>
                  <th>{t('hrAnalytics.insights.columns.confidence')}</th>
                </tr>
              </thead>
              <tbody>
                {themes.map((th) => (
                  <tr key={`${th.source}-${th.theme}`}>
                    <td>{t(`hrAnalytics.insights.sources.${th.source}`, th.source)}</td>
                    <td>{th.theme}</td>
                    <td>{t(`hrAnalytics.insights.sentiments.${th.sentiment}`, th.sentiment)}</td>
                    <td>{th.responseCount}</td>
                    <td>{ondalik(th.confidence, 2)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Scroller>
        )}
      </Card>

      <Card>
        <Title>{t('hrAnalytics.insights.anomalyTitle')}</Title>
        <Description>{t('hrAnalytics.insights.anomalyDescription')}</Description>
        <Controls>
          <GenericSelect
            label={t('hrAnalytics.insights.metricSelectorLabel')}
            options={metricOptions}
            value={metricCode}
            onChange={setMetricCode}
          />
        </Controls>
        {anomalyState === 'loading' ? (
          <StateMessage>{t('common.loading')}</StateMessage>
        ) : anomalyState === 'unavailable' ? (
          <StateMessage>{t('hrAnalytics.insights.componentUnavailable')}</StateMessage>
        ) : !anomaly || !anomaly.evaluated ? (
          // "Anomali yok" ile "değerlendirilemedi" FARKLI şeylerdir — backend bunu `evaluated`
          // ile açıkça ayırıyor, ekran da ayırmak zorunda.
          <StateMessage>{t('hrAnalytics.insights.anomalyNotEvaluated')}</StateMessage>
        ) : (
          <Scroller>
            <Table>
              <thead>
                <tr>
                  <th>{t('hrAnalytics.insights.columns.period')}</th>
                  <th>{t('hrAnalytics.insights.columns.value')}</th>
                  <th>{t('hrAnalytics.insights.columns.expected')}</th>
                  <th>{t('hrAnalytics.insights.columns.robustZ')}</th>
                  <th>{t('hrAnalytics.insights.columns.anomaly')}</th>
                </tr>
              </thead>
              <tbody>
                {anomaliNoktalari.map((p) => (
                  <tr key={p.periodStart}>
                    <td>{tarih(p.periodStart)}</td>
                    <td>{ondalik(p.value)}</td>
                    <td>{ondalik(p.expected)}</td>
                    <td>{ondalik(p.robustZ, 2)}</td>
                    <td>
                      {p.isAnomaly
                        ? t('hrAnalytics.insights.anomalyYes')
                        : t('hrAnalytics.insights.anomalyNo')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Scroller>
        )}
      </Card>

      <Card>
        <Title>{t('hrAnalytics.insights.qualityTitle')}</Title>
        <Description>{t('hrAnalytics.insights.qualityDescription')}</Description>
        {qualityState === 'loading' ? (
          <StateMessage>{t('common.loading')}</StateMessage>
        ) : qualityState === 'unavailable' ? (
          <StateMessage>{t('hrAnalytics.insights.componentUnavailable')}</StateMessage>
        ) : !kalite ? (
          // Kilitli/değerlendirilemez durumlar 200 + state ile geliyor; sebebini SÖYLÜYORUZ.
          <StateMessage>
            {t(`hrAnalytics.insights.qualityStates.${quality?.state ?? 'not_evaluable'}`,
              t('hrAnalytics.insights.qualityStates.not_evaluable'))}
          </StateMessage>
        ) : (
          <>
            <Preliminary>
              {t('hrAnalytics.insights.validationSummary', {
                window: quality.validation?.windowMonths,
                holdout: quality.validation?.holdoutMonths,
                testRows: quality.validation?.testRows,
                testEvents: quality.validation?.testEvents,
              })}
            </Preliminary>
            <Scroller>
              <Table>
                <thead>
                  <tr>
                    <th>{t('hrAnalytics.insights.columns.auc')}</th>
                    <th>{t('hrAnalytics.insights.columns.brier')}</th>
                    <th>{t('hrAnalytics.insights.columns.suppressedBins')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{ondalik(kalite.auc)}</td>
                    <td>{ondalik(kalite.brier, 4)}</td>
                    <td>{kalite.suppressedBins}</td>
                  </tr>
                </tbody>
              </Table>
            </Scroller>

            <Description style={{ marginTop: 16 }}>
              {t('hrAnalytics.insights.calibrationDescription')}
            </Description>
            <Scroller>
              <Table>
                <thead>
                  <tr>
                    <th>{t('hrAnalytics.insights.columns.band')}</th>
                    <th>{t('hrAnalytics.insights.columns.count')}</th>
                    <th>{t('hrAnalytics.insights.columns.meanPredicted')}</th>
                    <th>{t('hrAnalytics.insights.columns.observedRate')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(kalite.calibration ?? []).map((b) => (
                    <tr key={`${b.lower}-${b.upper}`}>
                      <td>{`${yuzde(b.lower)} – ${yuzde(b.upper)}`}</td>
                      <td>{b.count}</td>
                      <td>{yuzde(b.meanPredicted)}</td>
                      <td>{yuzde(b.observedRate)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Scroller>
          </>
        )}
      </Card>
    </Container>
  );
}
